import type { ReactElement } from 'react'
import type { SalesOrder, SalesOrderAssignee } from '@/lib/services/sales-orders/sales-orders.types'
import type { SelectInputOption } from '@/components/form/SelectInput'
import { useAppForm } from '@/lib/form'
import { useAbility, useIsReadOnly } from '@/lib/casl'
import { useUpdateSalesOrderMutation } from '@/lib/services/sales-orders/sales-orders.query'
import { useUsers } from '@/lib/services/users/users.query'

import withSuspense from '@/hooks/withSuspense'
import { toast } from 'sonner'
import { Skeleton } from '../../ui/skeleton'
import { Typography } from '@/components/ui/typography'
const AssignDesignerFormLoadingSkeleton = ({
  headerAction = false,
}: {
  headerAction?: boolean
}): ReactElement => {
  return <Skeleton className={headerAction ? 'h-10 w-36 rounded-full' : 'h-9 w-36'} />
}

const AssignDesignerFormError = () => {
  return <Typography variant="supporting">Error loading employees</Typography>
}

function AssignDesignerForm({
  order,
  headerAction = false,
}: {
  order: Pick<SalesOrder, 'id'> &
    Partial<Pick<SalesOrder, 'user_id' | 'assigned_user'>> & { user?: SalesOrderAssignee | null }
  headerAction?: boolean
}): ReactElement | null {
  // order.user_id is numeric or null, but select inputs use strings
  const currentlyAssignedDesignerId = order?.user_id?.toString() || '0'
  const assignedDesigner = order?.assigned_user ?? order?.user ?? null
  const ability = useAbility()
  const canViewAssignableUsers =
    ability.can('list', 'organization-users') ||
    ability.can('view', 'organization-users') ||
    ability.can('list', 'users') ||
    ability.can('view', 'users')
  const { data: employees = [], isPending: areEmployeesPending } = useUsers({
    params: { enabled: canViewAssignableUsers },
  })
  const { mutateAsync: updateSalesOrder } = useUpdateSalesOrderMutation()
  const { isReadOnly } = useIsReadOnly()

  const form = useAppForm({
    defaultValues: {
      user_id: currentlyAssignedDesignerId,
    },
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          await updateSalesOrder({
            id: order.id,
            user_id: value.user_id === '0' ? null : value.user_id,
          })
          toast.success(`Designer ${order?.user_id ? 'updated' : 'assigned'} successfully`)
        } catch (error) {
          console.error('Failed to assign designer:', error)
          toast.error('Failed to assign designer')
          formApi.reset()
        }
      },
    },
  })
  const hasAssignedDesigner = currentlyAssignedDesignerId !== '0'
  const canShowReadOnlyAssignedDesigner = isReadOnly && hasAssignedDesigner

  if (isReadOnly && !hasAssignedDesigner) {
    return null
  }

  if (!canViewAssignableUsers && !canShowReadOnlyAssignedDesigner) {
    return null
  }

  if (canViewAssignableUsers && areEmployeesPending) {
    return <AssignDesignerFormLoadingSkeleton headerAction={headerAction} />
  }

  const options: SelectInputOption[] = [
    {
      name: <Typography variant="supporting">Assign Designer</Typography>,
      id: '0',
    },
    ...employees,
  ]
  const hasAssignedDesignerOption = options.some(
    (option) => option?.id?.toString() === currentlyAssignedDesignerId
  )

  if (hasAssignedDesigner && !hasAssignedDesignerOption) {
    options.splice(1, 0, {
      id: currentlyAssignedDesignerId,
      name: assignedDesigner?.name || 'Assigned Designer',
    })
  }

  return (
    <form.AppForm>
      <form.Form>
        <form.AppField
          name="user_id"
          listeners={{
            onChange: ({ value }) => {
              // `value` is a string, and so is `currentlyAssignedDesignerId`
              if (value !== currentlyAssignedDesignerId && !isReadOnly) {
                void form.handleSubmit()
              }
            },
          }}
        >
          {(field) => (
            <field.SelectInput
              id={`assign-designer-${order?.id}`}
              options={options}
              className="w-full"
              classNames={{
                selectTrigger: headerAction ? 'h-10 rounded-full px-4' : '',
              }}
              size="default"
            />
          )}
        </form.AppField>
        <form.SubmitButton className="hidden">Submit</form.SubmitButton>
      </form.Form>
    </form.AppForm>
  )
}

export default withSuspense(AssignDesignerForm, {
  loading: AssignDesignerFormLoadingSkeleton,
  error: AssignDesignerFormError,
})
